/** Read-only facts a card shows beside its catalog fields (Access's sign-in facts, Origin's
 *  joined date, Technical's internal properties) — the look of a derived FieldSlot (a
 *  caption label, a muted value; no reserved input height), on the cards' field grid. They
 *  are the same in both modes, so they never move on Edit. */
import { Box, Typography } from '@mui/material';
import type { ReactNode } from 'react';

import { FieldHelp } from '@/components/FieldHelp';
import { FIELD_GRID_SX } from '@/features/records/panels/fieldGrid';

export interface Fact {
  key: string;
  label: string;
  /** null / '' render the em dash. */
  value: ReactNode;
  /** One line of help, in the label's popover. */
  help?: string;
  /** Takes the whole row. */
  wide?: boolean;
}

export function FactGrid({ facts }: { facts: Fact[] }) {
  return (
    <Box sx={FIELD_GRID_SX}>
      {facts.map((f) => {
        const empty = f.value === null || f.value === undefined || f.value === '';
        return (
          <Box key={f.key} data-fact={f.key} sx={{ minWidth: 0, pb: 1.5, ...(f.wide ? { gridColumn: '1 / -1' } : {}) }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minHeight: 24 }}>
              <Typography variant="caption" color="text.secondary">
                {f.label}
              </Typography>
              {f.help && <FieldHelp help={{ title: f.label, body: f.help }} />}
            </Box>
            <Typography
              variant="body2"
              component="div"
              sx={{ color: empty ? 'text.disabled' : 'text.secondary', overflowWrap: 'anywhere', minWidth: 0 }}
            >
              {empty ? '—' : f.value}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}
